import React, { useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { func } from "../../../utils";
import { Loading } from "../../../components";

import Section from "../../../components/atoms/Section";
import useWindowSize from "../../../hooks/useWindowSize";
import { ContentWrapper, InnerContentWrapper } from "./FaseKnockoutSOC.styled";
import KnockoutTable from "./components/KnockoutTable";
import {
  useFetchCurrentEventSeries,
  useFetchEventSeriesDetail,
  useFetchKnockoutBracket,
  useFetchRegionEventSeries,
} from "../../../networks";
import { restructureToKnockoutArray } from "./helper";
import { findCurrentRegion } from "../../../utils/region";

const FaseKnockoutSOC = ({ activeClassLevel }) => {
  const { city } = useParams();
  const { search } = useLocation();

  const activeCardType = new URLSearchParams(search).get("activeCard") === "futsalsmpGroup" ? 2 : 1;
  const isSMP = activeClassLevel?.toLowerCase() === "smp";
  const [activeCarousel, setActiveCarousel] = useState(activeCardType);
  const { size } = useWindowSize();

  const { data: dataRaw } = useFetchRegionEventSeries();

  const dataRegistCity = dataRaw?.data?.data?.eventSeries || [];

  // Musim berjalan ditentukan `event/current-series`, bukan tanggal terbaru di
  // region/series. Tanpa ini Tangerang ke-resolve ke region 2025 (id 42) dan
  // bagan knockout-nya nampilin pertandingan musim lalu, padahal region musim
  // ini (id 74) bracketnya masih kosong.
  const { data: dataCurrentSeriesRaw, isFetching: fetchingCurrentSeries } =
    useFetchCurrentEventSeries();
  const dataCurrentSeries = (
    dataCurrentSeriesRaw?.data?.data?.eventSeries || []
  ).flatMap((group) => group?.events || []);

  // Jangan resolve selagi current-series masih loading: kalau dibiarkan,
  // findCurrentRegion jatuh ke fallback lama dan data musim lalu sempat kedip.
  const dataCityAttribute = fetchingCurrentSeries
    ? undefined
    : findCurrentRegion(dataRegistCity, city, dataCurrentSeries);
  const { data: dataSeriesDetailRaw } = useFetchEventSeriesDetail("soc");

  const dataSeriesDetailRegionId = (
    dataSeriesDetailRaw?.data?.data?.eventSeries?.[0]?.region_series || []
  )?.find((item) => item?.is_soc)?.id;

  // Check if URL includes /soc and year parameter conditions
  const urlParams = new URLSearchParams(search);
  const yearParam = urlParams.get("year");
  const isSOCPath = window.location.pathname.includes("/soc");
  const shouldUseSOC2025 = isSOCPath && (yearParam === "2025" || !yearParam);

  const dataCityAttributeId = shouldUseSOC2025 ? dataSeriesDetailRegionId : dataCityAttribute?.id;

  const { data: dataKnockoutBracketRaw, isFetching } = useFetchKnockoutBracket(
    dataCityAttributeId,
    dataCityAttribute?.length,
  );

  // Kategori Kuliah dinamai "futsalKampus" di API, jadi label carousel ga bisa
  // langsung ditempel — sebelumnya jadi "futsalKuliah" dan bagannya selalu kosong.
  const categoryKey =
    { SMA: "futsalSMA", SMP: "futsalSMP", Kuliah: "futsalKampus" }[activeClassLevel] ||
    "futsalSMA";

  const dataClassmentsDetail =
    restructureToKnockoutArray(
      dataKnockoutBracketRaw?.data?.data || [],
      categoryKey,
      shouldUseSOC2025,
    ) ?? [];

  const dataKnockout = dataClassmentsDetail || [];

  const isMobileView = (size?.width || 0) <= 830;

  const getChampion = () => {
    const data = dataKnockout?.rounds
      ?.flat()
      ?.map((data) => data)
      ?.findLast((item) => !!item?.champion_name);

    return {
      first_place: data?.club_1st,
      second_place: data?.club_2nd,
      third_pace: data?.club_3rd,
    };
  };

  const championData = getChampion();
  const firstPlace = championData?.first_place;
  const secondPlace = championData?.second_place;
  const thirdPlace = championData?.third_pace;

  // Maskot + podium juara. Di desktop ikut masuk panel bareng bagan; di
  // mobile ditaruh di luar panel, soalnya panelnya selebar bagan (lebih lebar
  // dari layar) jadi maskotnya bakal ke-center di luar viewport.
  //
  // Naiknya pakai margin-top negatif, bukan `bottom-72`: `bottom` cuma
  // nggeser visual, layout-nya tetep di posisi lama, jadi panelnya nyisa
  // ratusan px kosong di bawah maskot.
  const winnerPodium = (
    <div className="relative flex items-center flex-col gap-4 mt-0 md:-mt-64">
      <div className="relative flex flex-col">
        {/* Aset 2026 rasionya 3:4 (aset lama 469x512), jadi tingginya
            ikut disesuaikan + object-contain biar ga kegencet. */}
        <img
          className="relative w-[234px] h-[312px] right-6 object-contain"
          src={func.getStaticImg("/illustration/piyu-2026.png")}
          alt="Maskot Piala by.U mengangkat piala"
        />
        <img
          className="relative w-[123px] h-[21px] mx-auto bottom-2 left-2"
          src={func.getStaticImg("/illustration/winner-soc.png")}
        ></img>
      </div>
      <div className="flex items-center w-[234px] h-[42px] rounded-full bg-[#f18729] overflow-hidden">
        <div
          className="bg-white w-[44px] h-full flex items-center justify-center"
          style={{
            clipPath: "polygon(0 0, 100% 0, 80% 100%, 0% 100%)",
          }}
        >
          <span className="text-[#222d3c] font-bold">1</span>
        </div>
        <div className="bg-[#f18729] flex-1 h-full flex items-center">
          <span className="font-bold text-[16px]/[18px] text-white">
            {firstPlace?.champion_name || "TBD"}
          </span>
        </div>
      </div>
      <div className="flex items-center w-[234px] h-[42px] rounded-full bg-[#f18729] overflow-hidden">
        <div
          className="bg-white w-[44px] h-full flex items-center justify-center"
          style={{
            clipPath: "polygon(0 0, 100% 0, 80% 100%, 0% 100%)",
          }}
        >
          <span className="text-[#222d3c] font-bold text-2xl">2</span>
        </div>
        <div className="bg-[#f18729] flex-1 h-full flex items-center">
          <span className="font-bold text-[16px]/[18px] text-white">
            {secondPlace?.runner_up_name || "TBD"}
          </span>
        </div>
      </div>
      <div className="flex items-center w-[234px] h-[42px] rounded-full bg-[#f18729] overflow-hidden">
        <div
          className="bg-white w-[44px] h-full flex items-center justify-center"
          style={{
            clipPath: "polygon(0 0, 100% 0, 80% 100%, 0% 100%)",
          }}
        >
          <span className="text-[#222d3c] font-bold text-2xl">3</span>
        </div>
        <div className="bg-[#f18729] flex-1 h-full flex items-center">
          <span className="font-bold text-[16px]/[18px] text-white">
            {thirdPlace?.third_place_name || "TBD"}
          </span>
        </div>
      </div>
      </div>
  );

  if (isFetching) return <Loading />;

  return (
    <>
      <Section id="klasemen-pertandingan" height={"auto"} overflowY={"auto"}>
        <ContentWrapper style={{ marginTop: 0 }}>
          {/* Bagannya sengaja dilepas dari batas 1440px InnerContentWrapper:
              StandingCardNew udah melebarkan area ini ke 100vw, jadi kalau di
              sini dikunci 1440px isinya malah overflow dan mentok ke kiri. */}
          <InnerContentWrapper style={isMobileView ? undefined : { maxWidth: "none" }}>
            {/* Arah flex ngikut isMobileView (830px), bukan `lg:` (1024px) —
                sebelumnya di rentang 831-1023px arahnya masih column, jadi
                justify-content nengahin vertikal dan bagannya rata kiri. */}
            <div
              style={
                isMobileView
                  ? { overflowX: "scroll" }
                  : { overflowX: "auto", justifyContent: "safe center" }
              }
              className={isMobileView ? "flex flex-col" : "flex flex-row"}
            >
              {/* Panel gelap: bagan, maskot, dan podium juara dibungkus jadi
                  satu blok. Sebelumnya semuanya nempel langsung ke background
                  halaman, jadi garis bracket-nya kelihatan ngambang. */}
              <div className="flex flex-col md:flex-row bg-[#0E2436]/90 rounded-3xl px-6 py-6 md:px-10 md:py-8 w-max">
                {/* Bagan dan catatannya satu kolom: catatannya ikut ke-center
                    bareng bagan, jadi tepi kirinya sejajar. Kalau ditaruh di luar
                    container ini, dia nempel ke kiri viewport sendirian. */}
                <div className="flex flex-col">
                <div className="flex flex-col md:flex-row">
                  <KnockoutTable
                    knockoutData={dataKnockout}
                    roundOf16={shouldUseSOC2025}
                    categoryKey={categoryKey}
                  />
                </div>
                {!isMobileView && (
                  <div className="flex flex-row items-center">
                    <img
                      src={func.getStaticImg(`icon/notes-red.svg`)}
                      alt="notes"
                      className="w-[12px] h-[12px] mt-3 mr-2 shrink-0"
                    />
                    <span className="text-[#DDE2E8] text-[16px] flex flex-row items-center mt-3">
                      Menang melalui adu pinalti
                    </span>
                  </div>
                )}
              </div>
              {isMobileView && (
                <div className="flex flex-row items-center px-[24px]">
                  <img
                    src={func.getStaticImg(`icon/notes-red.svg`)}
                    alt="notes"
                    className="w-[12px] h-[12px] mt-3 mr-2 shrink-0"
                  />
                  <span className="text-[#DDE2E8] text-[16px] flex flex-row items-center mt-3">
                    Menang melalui adu pinalti
                  </span>
                </div>
              )}
              {!isMobileView && winnerPodium}
              </div>
              {isMobileView && winnerPodium}
            </div>
          </InnerContentWrapper>
        </ContentWrapper>
      </Section>
    </>
  );
};

export default FaseKnockoutSOC;
